'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

export type InboxTheme = 'orange' | 'violet' | 'emerald' | 'rose' | 'sky';

interface ThemeConfig {
  id: InboxTheme;
  label: string;
  color: string;
  gradient: string;
  textColor: string;
}

export const INBOX_THEMES: ThemeConfig[] = [
  { id: 'orange', label: 'Cosmic Orange', color: '#E8600A', gradient: 'linear-gradient(135deg, #E8600A, #FF8C42)', textColor: '#fff8f0' },
  { id: 'violet', label: 'Deep Violet', color: '#5B4FE8', gradient: 'linear-gradient(135deg, #5B4FE8, #7C3AED)', textColor: '#f5f3ff' },
  { id: 'emerald', label: 'Forest Green', color: '#059669', gradient: 'linear-gradient(135deg, #059669, #10B981)', textColor: '#f0fdf4' },
  { id: 'rose', label: 'Crimson Rose', color: '#E11D48', gradient: 'linear-gradient(135deg, #E11D48, #F43F5E)', textColor: '#fff1f2' },
  { id: 'sky', label: 'Ocean Sky', color: '#0284C7', gradient: 'linear-gradient(135deg, #0284C7, #0EA5E9)', textColor: '#f0f9ff' },
];

interface InboxThemeContextType {
  theme: InboxTheme;
  setTheme: (t: InboxTheme) => void;
  themeConfig: ThemeConfig;
}

const InboxThemeContext = createContext<InboxThemeContextType>({
  theme: 'orange',
  setTheme: () => {},
  themeConfig: INBOX_THEMES[0],
});

export function InboxThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<InboxTheme>('orange');

  useEffect(() => {
    const saved = localStorage.getItem('inbox-theme') as InboxTheme | null;
    if (saved && INBOX_THEMES.find((t) => t.id === saved)) {
      setThemeState(saved);
    }
  }, []);

  const setTheme = (t: InboxTheme) => {
    setThemeState(t);
    localStorage.setItem('inbox-theme', t);
  };

  const themeConfig = INBOX_THEMES.find((t) => t.id === theme) ?? INBOX_THEMES[0];

  return (
    <InboxThemeContext.Provider value={{ theme, setTheme, themeConfig }}>
      {children}
    </InboxThemeContext.Provider>
  );
}

export function useInboxTheme() {
  return useContext(InboxThemeContext);
}
